import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Container, Typography, Box, Avatar, Chip, Stack, Skeleton, Alert, Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Surface, StatCard, DirectionChip, ConflictBadge } from '../components/primitives';

const fmtCompactUsd = (n) => {
    if (n == null || n === 0) return '—';
    if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}T`;
    if (n >= 1e9)  return `$${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6)  return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3)  return `$${(n / 1e3).toFixed(0)}K`;
    return `$${n.toLocaleString()}`;
};

export default function IssuerDetail() {
    const { ticker } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Separate state for trades-fetch failure: the aggregate may load fine
    // while the trades feed fails. We render the aggregate either way and
    // surface a small inline alert above the (empty) trades section instead
    // of swallowing the failure silently.
    const [tradesError, setTradesError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            setTradesError(null);
            try {
                // Parallel fetch: aggregate (metadata + politicians) from the
                // precomputed S3 payload, plus the trade rows from the
                // DynamoDB-backed /v1/issuers/{ticker}/trades endpoint.
                const safeTicker = encodeURIComponent(ticker);
                const [aggRes, tradesRes] = await Promise.all([
                    fetch(`https://api.capitolgains.io/v1/issuers/${safeTicker}`),
                    fetch(`https://api.capitolgains.io/v1/issuers/${safeTicker}/trades?limit=200`),
                ]);
                if (!aggRes.ok) {
                    const errBody = await aggRes.json().catch(() => ({}));
                    throw new Error(errBody?.error?.message || 'Failed to load issuer');
                }
                const aggEnvelope = await aggRes.json();

                let trades = [];
                if (tradesRes.ok) {
                    const tradesEnvelope = await tradesRes.json();
                    trades = tradesEnvelope.data || [];
                } else {
                    const errBody = await tradesRes.json().catch(() => ({}));
                    if (!cancelled) {
                        setTradesError(errBody?.error?.message || 'Could not load recent trades.');
                    }
                }

                if (cancelled) return;
                setData({ ...aggEnvelope.data, trades });
            } catch (e) {
                if (!cancelled) setError(e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchData();
        return () => { cancelled = true; };
    }, [ticker]);

    if (loading) {
        return (
            <Container sx={{ pt: 6, pb: 8 }}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 3 }} />
                <Skeleton variant="rectangular" height={92} sx={{ borderRadius: 2, mb: 3 }} />
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Container>
        );
    }

    if (error) {
        return (
            <Container sx={{ pt: 6, pb: 8 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    const { metadata, stats, politicians, trades } = data;

    return (
        <Container sx={{ pt: 6, pb: 8 }}>
            {/* Back link */}
            <Box
                component={Link}
                to="/issuers"
                sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    color: 'text.muted', textDecoration: 'none', mb: 2,
                    '&:hover': { color: 'text.secondary' },
                }}
            >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">All issuers</Typography>
            </Box>

            {/* Header card */}
            <Surface sx={{ mb: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                    <Avatar
                        src={`https://img.logo.dev/ticker/${ticker}?token=pk_Fy4ZV5RMQEKzNJfSy9h6BA`}
                        alt={ticker}
                        sx={{ width: 64, height: 64, bgcolor: '#fff', flexShrink: 0 }}
                    />
                    <Box flex={1} minWidth={0}>
                        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                            <Typography
                                variant="h1"
                                sx={{
                                    color: 'text.primary',
                                    fontFamily: t => t.typography.fontFamilyMono,
                                }}
                            >
                                {ticker}
                            </Typography>
                            {metadata?.sector && (
                                <Chip
                                    label={metadata.sector}
                                    size="small"
                                    sx={{
                                        bgcolor: 'custom.surface.elevated',
                                        color: 'text.secondary',
                                        border: t => `1px solid ${t.palette.custom.border.subtle}`,
                                    }}
                                />
                            )}
                        </Stack>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                            {metadata?.long_name || 'Unknown company'}
                            {metadata?.industry && ` · ${metadata.industry}`}
                            {metadata?.market_cap && ` · ${fmtCompactUsd(metadata.market_cap)} market cap`}
                        </Typography>
                        {metadata?.website && (
                            <Typography
                                component="a"
                                href={metadata.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="caption"
                                sx={{
                                    color: 'primary.light',
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    mt: 1,
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                {metadata.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                <OpenInNewIcon sx={{ fontSize: 12 }} />
                            </Typography>
                        )}
                        {metadata?.summary && (
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.muted', display: 'block', mt: 1.5, lineHeight: 1.5 }}
                            >
                                {metadata.summary}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </Surface>

            {/* Stat strip */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={6} md={3}>
                    <StatCard label="Trades" value={stats.trade_count.toLocaleString()} mono />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard label="Politicians" value={stats.unique_members} mono />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard label="Buys" value={stats.buy_count} mono tone="positive" />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard label="Sells" value={stats.sell_count} mono tone="negative" />
                </Grid>
            </Grid>

            {/* Politicians who traded */}
            <Typography variant="h3" sx={{ color: 'text.primary', mb: 2 }}>
                Politicians who traded {ticker}
            </Typography>
            <Surface padding={0} sx={{ mb: 4 }}>
                {politicians.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.muted' }}>
                            No trades visible.
                        </Typography>
                    </Box>
                )}
                {politicians.map((p, i) => {
                    const clickable = Boolean(p.member_id);
                    return (
                        <Box
                            key={p.member_id || `${p.first_name}_${p.last_name}_${i}`}
                            component={clickable ? Link : 'div'}
                            to={clickable ? `/congressmember/${p.member_id}` : undefined}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr 1fr',
                                alignItems: 'center',
                                gap: 2,
                                px: 3, py: 1.5,
                                textDecoration: 'none',
                                borderBottom: t => i < politicians.length - 1
                                    ? `1px solid ${t.palette.custom.border.subtle}`
                                    : 'none',
                                transition: t => `background-color ${t.customTransitions?.fast || '150ms ease'}`,
                                '&:hover': clickable ? { bgcolor: 'custom.surface.elevated' } : undefined,
                            }}
                        >
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                        {p.first_name} {p.last_name}
                                    </Typography>
                                    {p.max_conflict && (
                                        <ConflictBadge
                                            conflicts={[{
                                                confidence: p.max_conflict,
                                                committee_name: p.max_conflict_committee || 'Committee oversight',
                                                matched_on: 'oversight of',
                                                matched_value: `${ticker} sector`,
                                            }]}
                                        />
                                    )}
                                </Stack>
                                <Typography variant="caption" sx={{ color: 'text.muted' }}>
                                    {p.chamber}
                                </Typography>
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', fontFamily: t => t.typography.fontFamilyMono }}
                            >
                                {p.trade_count} trade{p.trade_count !== 1 ? 's' : ''}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'custom.performance.positive',
                                        fontFamily: t => t.typography.fontFamilyMono,
                                    }}
                                >
                                    {p.buy_count} buy{p.buy_count !== 1 ? 's' : ''}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'custom.performance.negative',
                                        fontFamily: t => t.typography.fontFamilyMono,
                                    }}
                                >
                                    {p.sell_count} sell{p.sell_count !== 1 ? 's' : ''}
                                </Typography>
                            </Stack>
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.muted', fontFamily: t => t.typography.fontFamilyMono }}
                            >
                                last {p.last_trade_date || '—'}
                            </Typography>
                        </Box>
                    );
                })}
            </Surface>

            {/* Recent trades feed */}
            <Typography variant="h3" sx={{ color: 'text.primary', mb: 2 }}>
                Recent trades
            </Typography>
            {tradesError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {tradesError}
                </Alert>
            )}
            <Surface padding={0}>
                {trades.length === 0 && !tradesError && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'text.muted' }}>
                            No trades to show.
                        </Typography>
                    </Box>
                )}
                {trades.map((t, i) => (
                    <Box
                        key={`${t.transaction_date}-${t.first_name}-${t.last_name}-${i}`}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 0.7fr 1.2fr 1fr',
                            alignItems: 'center',
                            gap: 2,
                            px: 3, py: 1.5,
                            borderBottom: th => i < trades.length - 1
                                ? `1px solid ${th.palette.custom.border.subtle}`
                                : 'none',
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                {t.first_name} {t.last_name}
                            </Typography>
                            <ConflictBadge conflicts={t.conflicts} />
                        </Stack>
                        <DirectionChip direction={t.transaction} />
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', fontFamily: th => th.typography.fontFamilyMono }}
                        >
                            ${(t.asset_value_low || 0).toLocaleString()}–${(t.asset_value_high || 0).toLocaleString()}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.muted', fontFamily: th => th.typography.fontFamilyMono }}
                        >
                            {t.transaction_date}
                        </Typography>
                    </Box>
                ))}
            </Surface>
        </Container>
    );
}
